// Reusable checkout-gateway selector.
//
// Loads /api/public/payment-gateways (active only, no auth needed) and renders the
// list as selectable cards. Auto-selects the lowest-priority gateway by default.
//
// Wraps a children "fallback strip" when no gateway is configured yet so the existing
// env-based Razorpay flow keeps working — never blocks checkout.
import React, { useEffect, useState } from 'react';
import { ShieldCheck, CheckCircle2, Activity, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';

const HEALTH_DOT = {
  healthy: 'bg-green-500',
  slow:    'bg-amber-500',
  failed:  'bg-red-500',
  unknown: 'bg-gray-400',
};

const PROVIDER_LABEL = {
  razorpay: 'Razorpay', cashfree: 'Cashfree', payu: 'PayU', stripe: 'Stripe',
  phonepe: 'PhonePe', paytm: 'Paytm', ccavenue: 'CCAvenue',
};

const CheckoutGatewaySelector = ({ value, onChange, fallback }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/public/payment-gateways')
      .then((r) => {
        if (!alive) return;
        const list = r.data?.items || [];
        setItems(list);
        // Auto-select the first (lowest-priority) gateway if none selected
        if (list.length > 0 && !value) onChange?.(list[0]);
      })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch state or zero-active gateways → fallback to legacy Razorpay block
  if (items === null) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse" data-testid="pg-selector-loading">
        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
        <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded" />
      </div>
    );
  }
  if (error || items.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className="space-y-3" data-testid="pg-selector">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
        Choose your payment method
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((g) => {
          const selected = value?.id === g.id;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onChange?.(g)}
              data-testid={`pg-option-${g.id}`}
              className={`text-left rounded-xl border p-3 transition-colors ${selected ? 'border-red-600 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500/20' : 'border-gray-200 dark:border-gray-700 hover:border-red-300'}`}
            >
              <div className="flex items-center gap-3">
                {g.logo_url ? (
                  <img src={g.logo_url} alt="" className="h-8 w-8 object-contain" />
                ) : (
                  <div className={`h-8 w-8 rounded-lg grid place-items-center text-[10px] font-bold uppercase ${selected ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                    {(g.name || g.provider).slice(0, 2)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{g.name || PROVIDER_LABEL[g.provider] || g.provider}</span>
                    <span className={`h-1.5 w-1.5 rounded-full ${HEALTH_DOT[g.health] || HEALTH_DOT.unknown}`} title={`Health: ${g.health || 'unknown'}`} />
                    {selected && <CheckCircle2 className="h-4 w-4 text-red-600 ml-auto" />}
                  </div>
                  <p className="text-[11px] text-gray-500 truncate">
                    {(g.payment_methods || []).slice(0, 4).join(' · ') || g.currency}
                    {g.environment === 'sandbox' && <span className="ml-1 text-amber-600 font-semibold">· test</span>}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
        <Activity className="h-3 w-3" /> Live availability checked across {items.length} active provider{items.length === 1 ? '' : 's'}. Lowest priority wins by default.
      </p>
    </div>
  );
};

export default CheckoutGatewaySelector;
